// Liquid DnB / lofi jungle (docs/AUDIO_DESIGN.md §12): a soft, low-passed breakbeat at 160-176 BPM (the two-step, rolling
// ghost-snare breaks, 16th hats with 32nd ratchets, an Amen-like shuffle in upbeat) under slow, dreamy harmony: m9 / maj9 /
// m11 / add9 / 9sus4 pads held for one, two or four bars, chiming on the comping cells, a deep sine sub that holds and
// glides into the roots (rolling 8ths in upbeat), and sometimes an airy lead or a glassy pluck with sparse long notes in a
// ping-pong echo. Instruments are in dnb-voices.js.
import { QUALITIES, voice as rootless } from '../harmony.js';
import { createLiquidPad, createSub, createBreakKit, createLiquidLead } from './dnb-voices.js';

const mod12 = n => ((n % 12) + 12) % 12;
const ENERGY = { chill: 0, balanced: 1, upbeat: 2 };
const pickW = (r, w) => {
  let u = r() * Object.values(w).reduce((a, b) => a + b, 0);
  for (const [k, v] of Object.entries(w)) if ((u -= v) < 0) return k;
  return Object.keys(w)[0];
};

/* ---------------- pad voicing: the root under 4 open upper tones ---------------- */
// Candidates for a chord (root pc, quality): the root at MIDI 43–54 (an octave over the sub), then 4 chord tones from a
// 4th to a 10th above it, 2–7 semitones apart (3+ below MIDI 60), the top at 66–80; the 3rd (or the sus tone), the 7th /
// 6th and the colour tone (♯11, the 11 of m11, else the 9th) always there, 4+ pitch classes. → [{ v, mean (upper), ninth }].
const CANDS = new Map();
function padCands(root, q) {
  const id = `${root}:${q}`;
  if (CANDS.has(id)) return CANDS.get(id);
  const iv = [...new Set(QUALITIES[q].map(mod12))], has = i => iv.includes(i), pcs = iv.map(i => mod12(root + i));
  const need = [[4, 3, 5, 2].find(has), [11, 10, 9].find(has), has(6) ? 6 : has(3) && has(5) ? 5 : has(2) ? 2 : null]
    .filter(i => i != null).map(i => mod12(root + i));
  const ninth = has(2) ? mod12(root + 2) : null, out = [];
  const gap = (lo, hi) => hi - lo >= (lo < 60 ? 3 : 2) && hi - lo <= 7;
  for (let b = 43 + mod12(root - 43); b <= 54; b += 12) {
    const pool = [];
    for (let m = b + 5; m <= 80; m++) if (pcs.includes(m % 12)) pool.push(m);
    const n = pool.length;
    for (let i = 0; i < n && pool[i] - b <= 16; i++) for (let j = i + 1; j < n; j++) {
      if (!gap(pool[i], pool[j])) continue;
      for (let k = j + 1; k < n; k++) {
        if (!gap(pool[j], pool[k])) continue;
        for (let l = k + 1; l < n; l++) {
          if (!gap(pool[k], pool[l]) || pool[l] < 66) continue;
          const v = [b, pool[i], pool[j], pool[k], pool[l]], pc = v.map(m => m % 12);
          if (new Set(pc).size < 4 || !need.every(x => pc.includes(x))) continue;
          out.push({ v, mean: (v[1] + v[2] + v[3] + v[4]) / 4, ninth: ninth != null && pc.includes(ninth), dup: 4 - new Set(pc.slice(1)).size });
        }
      }
    }
  }
  CANDS.set(id, out);
  return out;
}
/** Cheapest candidate: Σ|Δ| of the upper tones + .4·|Δroot| + 1.5·max(0, |Δtop| − 3) + .3·|mean − 65| + .25·|root − 48| −
 *  1.5 with the 9th + 3 per pitch class the upper tones double. The track's first chord pays .4·|mean − (65 + register)| +
 *  .5·|top − (74 + register)| instead of the motion terms (plan.register, so tracks don't all open on the same notes). A
 *  chord that can keep its voicing keeps it. */
function padVoicing(sp, prev, c, plan) {
  const cands = padCands(mod12(plan.key.tonic + c.root), c.q);
  if (!cands.length) return rootless(sp, prev, plan.register);
  if (prev && prev.length === 5 && cands.some(({ v }) => v.every((m, i) => m === prev[i]))) return prev.slice();
  const reg = plan.register || 0;
  let best = cands[0].v, bc = Infinity;
  for (const { v, mean, ninth, dup } of cands) {
    let k = 0.25 * Math.abs(v[0] - 48) - (ninth ? 1.5 : 0) + 3 * dup;
    if (prev && prev.length === 5) {
      k += 0.3 * Math.abs(mean - 65) + 0.4 * Math.abs(v[0] - prev[0]) + 1.5 * Math.max(0, Math.abs(v[4] - prev[4]) - 3);
      for (let i = 1; i < 5; i++) k += Math.abs(v[i] - prev[i]);
    } else k += 0.4 * Math.abs(mean - 65 - reg) + 0.5 * Math.abs(v[4] - 74 - reg);
    if (k < bc) { bc = k; best = v; }
  }
  return best.slice();
}

/* ---------------- sub bass ---------------- */
// Cells, one letter a 16th: R root · o octave (short) · . rest; 'kick' follows the kick grid. The sub sits at MIDI
// 30–42 (46–92 Hz), nearest the last root.
const BASS_CELLS = {
  hold:   'R...............',
  two:    'R.........R.....',
  sync:   'R.....R...R.....',
  roll:   'R.R.R.R.R.R.R.R.',
  bounce: 'R.R.o.R.R.R.o.R.',
};
//                 per energy: weights of the track's main cell (the second one is drawn from the same table)
const BASS_POOL = [
  { hold: 1.4, two: 0.8, kick: 0.4 },
  { hold: 0.8, two: 1, kick: 0.8, sync: 0.6 },
  { roll: 1.2, sync: 0.8, bounce: 0.6, two: 0.6, kick: 0.5 },
];
const subNote = (pc, prev = 36) => {
  let best = 30 + mod12(pc - 30), bd = Infinity;
  for (let n = best; n <= 42; n += 12) { const d = Math.abs(n - prev); if (d < bd) { bd = d; best = n; } }
  return best;
};
/**
 * One bar of sub. The groove (mode 'kick'): the track's cell (another on 15 % of bars, 50 % of phrase ends), a root on every
 * chord change, and a glide into the next root an 8th early (step 14, p .3/.4/.5). Legato tracks hold each note to the
 * next (+0.3 step, so it re-articulates without a gap), staccato ones for .6 of it. Intro, break and outro: the roots held
 * for their chords, the final one 30 steps. slide = true on the same pitch is a tie (dnb-voices.js createSub): a bar whose
 * chord goes on (or which the last bar glided into) keeps the note the last bar held to its end.
 */
function subLine({ plan, st, i, b, mode, chords, nextChords, chordAt, kicks, stop }) {
  const D = plan.dnb.bass, r = st.rS, pc = c => mod12(plan.key.tonic + c.root), end = stop ?? 16, legato = plan.bass.style === 'legato';
  const into = st.dnbBar === i - 1 ? st.dnbHeld : null;               // the note the last bar held to its end, if any
  let hits;
  if (mode === 'kick') {
    const id = b.j % 4 === 3 ? D.cells[r() < 0.5 ? 1 : 0] : D.cells[r() < 0.85 ? 0 : 1];
    hits = id === 'kick' ? kicks.map(s => [s, 'R']) : [...BASS_CELLS[id]].map((c, s) => [s, c]).filter(([, c]) => c !== '.');
    for (const c of chords) { const s0 = c.start * 4; hits = hits.filter(([s]) => s !== s0); hits.push([s0, 'R']); }
    hits.sort((x, y) => x[0] - y[0]);
    const nx = nextChords && nextChords[0];
    if (nx && pc(nx) !== pc(chords[chords.length - 1]) && r() < D.approach) { hits = hits.filter(([s]) => s < 14); hits.push([14, 'A']); }
  } else if (mode === 'outro' && b.j === 2) hits = [[0, 'F']];
  else hits = chords.map(c => [c.start * 4, 'R']);
  hits = hits.filter(([s]) => s < end);
  st.dnbBar = i; st.dnbHeld = null;
  return hits.map(([s, ch], k) => {
    const c = chordAt(s), root = subNote(pc(c), st.prevBass), next = k + 1 < hits.length ? hits[k + 1][0] : end;
    let midi = root, len = next - s, vel = s === 0 ? 0.9 : s % 4 ? 0.74 : 0.82, slide = false;
    if (ch === 'o') { midi = root + 12; len = Math.min(len, 1.5); vel = 0.68; }
    else if (ch === 'A') { midi = subNote(pc(nextChords[0]), root); vel = 0.8; slide = true; len += 0.3; }
    else if (ch === 'F') len = 30;
    else { len = mode !== 'kick' || legato ? len + 0.3 : len * 0.6; st.prevBass = root; }
    if (s === 0 && into === midi && (!c.onset || st.dnbGlide)) slide = true;   // tie over the bar line
    if (k === hits.length - 1 && s + len >= 16) { st.dnbHeld = midi; st.dnbGlide = ch === 'A'; }
    return { s, midi, vel, dur: Math.max(0.5, len), slide };
  });
}

/* ---------------- lead: sparse long notes (the motif at 170 BPM would be a flurry) ---------------- */
const LCELLS = [[0, 12], [0, 8, 20], [4, 16], [0, 10, 22], [2, 14, 24], [0, 6, 16], [0, 16], [8, 20]];   // 2-bar cells (16ths)

export default {
  id: 'dnb', name: 'Liquid DnB', desc: 'Rolling breakbeats at 170 under dreamy pads and a deep, gliding sub',
  //            chill                                   balanced                                  upbeat
  vary: 'colour',
  energy: {
    stretch:  [0.6,                                     0.45,                                     0.3],            // odds of 2-bar chords
    bpm:      [[160, 166],                              [164, 172],                               [170, 176]],
    swing:    [[51, 54],                                [50, 53],                                 [50, 52]],
    snareLag: [[0.004, 0.01],                           [0.003, 0.008],                           [0.002, 0.006]],
    groove:   [[{ glide: 1.2, two: 1, roll: 0.3 }, { glide: 1.2, two: 1, roll: 0.3 }],
               [{ two: 1, roll: 1, step: 0.7, glide: 0.5 }, { two: 1, roll: 1, step: 0.7, glide: 0.5 }],
               [{ roll: 1.2, step: 1, amen: 0.8, two: 0.5 }, { roll: 1.2, step: 1, amen: 0.8, two: 0.5 }]],
    drumLevel:[0.7,                                     0.8,                                      0.85],
    kitLp:    [[3800, 5200],                            [4400, 6200],                             [5000, 7000]],
    epLp:     [[1100, 1750],                            [1300, 2100],                             [1550, 2600]],   // the pad bus LP
    strum:    [[0.03, 0.05],                            [0.02, 0.04],                             [0.015, 0.03]],
    epVel:    [[0.5, 0.6],                              [0.54, 0.64],                             [0.56, 0.68]],
    comp:     [{ hold: [1, 1.4], lift: [0.3, 0.7], sparkle: [0.2, 0.5], pulse: [0, 0.2], stab: [0, 0] },
               { hold: [0.6, 1], lift: [0.4, 0.8], sparkle: [0.3, 0.7], pulse: [0.2, 0.5], stab: [0, 0.25] },
               { hold: [0.2, 0.5], lift: [0.4, 0.8], sparkle: [0.3, 0.6], pulse: [0.4, 0.8], stab: [0.4, 0.9] }],
    push:     [0.06,                                    0.12,                                     0.2],
    legato:   [0.85,                                    0.7,                                      0.45],
    lead:     [0.5,                                     0.6,                                      0.7],
    softLead: [0.65,                                    0.55,                                     0.45],           // 'soft' plays the air voice, else the pluck
    tone:     [[4400, 6200],                            [5200, 7400],                             [6000, 8400]],
    wowCents: [[5, 9],                                  [4, 8],                                   [3, 7]],
    dust:     [[0.8, 1.4],                              [0.7, 1.2],                               [0.6, 1.1]],
    introHats:[0.5,                                     0.65,                                     0.8],
    leadFrom: [2,                                       2,                                        1],
    hatsFirst:[0.3,                                     0.22,                                     0.15],
    breakDrums:[0.4,                                    0.55,                                     0.7],
    breakLead:[0.35,                                    0.45,                                     0.55],
    // development (arrange.js BY_ENERGY): no solo (the lead's long notes); the beat or the band first
    solo:       [0, 0, 0],
    intro:      [{ keys: 0.3, beat: 0.3, groove: 0.2, lead: 0.1, cold: 0.1 }, { keys: 0.2, beat: 0.35, groove: 0.25, lead: 0.05, cold: 0.15 }, { keys: 0.1, beat: 0.35, groove: 0.3, lead: 0, cold: 0.25 }],
  },
  progressions: {
    major: {                                                                                     // 2 or 4 chords a 4-bar phrase
      L1: [[0, 'maj9', 8], [5, 'maj9', 8]],                                                      // Imaj9 · IVmaj9
      L2: [[9, 'm9', 4], [5, 'maj9', 4], [0, 'maj9', 4], [7, '9sus4', 4]],                       // vi9 · IVmaj9 · Imaj9 · V9sus4
      L3: [[5, 'maj9', 8], [4, 'm7', 4], [9, 'm9', 4]],                                          // IVmaj9 · iii7 · vi9
      L4: [[2, 'm9', 8], [0, 'maj9', 8]],                                                        // ii9 · Imaj9
      L5: [[0, 'maj9', 4], [9, 'm9', 4], [5, 'maj9s11', 8]],                                     // Imaj9 · vi9 · IVmaj9♯11
      L6: [[5, 'maj9', 8], [7, '9sus4', 4], [9, 'm11', 4]],                                      // IVmaj9 · V9sus4 · vi11
    },
    minor: {
      l1: [[0, 'm9', 8], [8, 'maj9', 8]],                                                        // i9 · ♭VImaj9
      l2: [[0, 'm11', 8], [5, 'm9', 8]],                                                         // i11 · iv9
      l3: [[0, 'm9', 4], [8, 'maj9', 4], [3, 'maj9', 4], [10, 'add9', 4]],                       // i9 · ♭VImaj9 · ♭IIImaj9 · ♭VIIadd9
      l4: [[8, 'maj9', 8], [10, '9sus4', 4], [0, 'm9', 4]],                                      // ♭VImaj9 · ♭VII9sus4 · i9
      l5: [[0, 'm9', 8], [10, 'add9', 4], [8, 'maj9s11', 4]],                                    // i9 · ♭VIIadd9 · ♭VImaj9♯11
      l6: [[5, 'm9', 4], [7, 'm7', 4], [0, 'm11', 8]],                                           // iv9 · v7 · i11
    },
  },
  turnarounds: { major: [[5, 'maj9', 2], [7, '9sus4', 2]], minor: [[8, 'maj9', 2], [10, 'add9', 2]] },   // IV|V9sus4 · ♭VI|♭VII
  // grids at 170: the snare on 2 and 4 (steps 4, 12), a second kick on the "and" of 3; W is the ride and T the 32nd hat
  // ratchet (this kit plays both lanes itself)
  grids: {
    //       0123456789abcdef
    two:   { K: 'x.........x.....', S: '....x.......x...', H: 'x.x.x.x.x.x.x.x.', X: '.s.s.s.s.s.s.s.s' },   // the two-step
    glide: { K: 'x.........x.....', S: '....x.......x...', W: 'x.s.x.s.x.s.x.s.', H: '..s...s...s...s.' },   // ride-led
    roll:  { K: 'x.........x.....', S: '....x..g.g..x..g', H: 'xxxxxxxxxxxxxx.x', T: '..............x.' },   // rolling ghosts
    step:  { K: 'x.x.......x.....', S: '....x.......x...', H: 'x.xxx.xxx.xxx.x.', R: '.......s.......s', T: '..............x.' },
    amen:  { K: 'x.x.......xx....', S: '....x..s.s..x..s', W: 'x.x.x.x.x.x.x.x.', H: '.s.s.s.s.s.s.s.s' },   // an Amen-like shuffle
    break: { K: 'x...............', S: '........x.......', W: 'x.s.x.s.x.s.x.s.' },                          // halftime: the snare on 3
  },
  fills: {
    roll: { roll: [[12, 0.62], [13, 0.34], [14, 0.5], [15, 0.72]] },                                       // snare roll-up
    run:  { roll: [[8, 0.3], [10, 0.36], [11, 0.4], [12, 0.5], [13, 0.44], [14, 0.6], [15, 0.74]] },       // a 16th run from beat 3
    rat:  { roll: [[12, 0.55], [13, 0.5], [14, 0.65], [15, 0.75]], voice: 'tom' },                         // 32nd hat ratchets
    stop: { stop: 12 },                                                                                     // the break drops, the pad rings
  },
  // comping cells [step, length, vel, pick] (0 = the root, -1 = the top): held pads that chime again on the cells
  comps: {
    hold:    [[0, 16]],                                                              // held (tied over the bar line)
    lift:    [[0, 16, 1], [10, 6, 0.75, [-2, -1]]],                                  // the top two chime on the "and" of 3
    sparkle: [[0, 16, 1, [0, 1, 2, 3]], [4, 12, 0.7, [-1]], [8, 8, 0.62, [-2]], [12, 4, 0.66, [-1]]],   // the top arrives on 2
    pulse:   [[0, 8, 1], [8, 8, 0.85]],                                              // struck again on 3
    stab:    [[0, 3, 1], [3, 3, 0.8], [6, 4, 0.85], [10, 6, 0.8]],                   // keys on 1, the "a" of 1, 2-and, 3-and
  },
  keysLevel: 1.6,                                                    // Chords only: +4.1 dB (the pad alone sits 4-5 dB under the band)
  plan(plan, { r, energy }) {
    const e = ENERGY[energy] ?? 1, pick = (a, b, d = 3) => +(a + r() * (b - a)).toFixed(d);
    const rate = pick(0.3, 0.6, 2), cents = pick(5, 9, 1);
    plan.dnb = {
      pad: {
        atk: pick(...[[0.22, 0.36], [0.14, 0.24], [0.08, 0.16]][e]), rel: pick(...[[0.7, 1], [0.55, 0.8], [0.4, 0.6]][e], 2),
        det: pick(7, 12, 1), bell: pick(0.25, 0.45, 2), bellDecay: pick(0.3, 0.5), swell: pick(1.25, 1.55, 2), swellTau: pick(0.3, 0.6),
        width: pick(0.25, 0.45, 2), chorus: { rate, depth: +((Math.pow(2, cents / 1200) - 1) / (2 * Math.PI * rate)).toFixed(6) },
      },
      bass: {
        cells: [pickW(r, BASS_POOL[e]), pickW(r, BASS_POOL[e])], approach: [0.3, 0.4, 0.5][e],
        oct: pick(0.12, 0.22, 2), drive: pick(1.4, 2.2, 2), lp: pick(300, 420, 0), glide: pick(0.04, 0.08), rel: pick(0.05, 0.09),
      },
      lead: { vib: pick(10, 16, 1), glide: pick(0.04, 0.07), echo: pick(0.28, 0.4, 2), fb: pick(0.38, 0.5, 2), index: pick(1.6, 2.6, 2) },
    };
    Object.assign(plan.drums.kit, {
      kickF0: pick(110, 130, 1), kickF1: pick(45, 52, 1), kickSweep: pick(0.02, 0.032), kickDecay: pick(...[[0.15, 0.2], [0.13, 0.18], [0.12, 0.16]][e]),
      snareTone: pick(185, 215, 0), snareF: pick(2400, 3400, 0), snareDecay: pick(...[[0.1, 0.15], [0.09, 0.13], [0.08, 0.12]][e]),
      hatHP: pick(5800, 7400, 0), hatClosed: pick(0.008, 0.013), openDecay: pick(0.1, 0.16), rideDecay: pick(0.3, 0.45),
      shakerDecay: pick(0.02, 0.03), grit: pick(1.2, 2, 2),
    });
    // the lead: 'soft' → the air voice, else the pluck; the motif thinned to 2-3 long notes over its 2 bars
    const steps = LCELLS[Math.floor(r() * LCELLS.length)], n = steps.length, last = 10 + Math.floor(r() * 7), vel = steps.map((s, i) => (i ? pick(0.6, 0.7, 2) : 0.76));
    if (plan.lead) {
      const m = plan.lead.motif;
      plan.lead.voice = plan.lead.timbre === 'soft' ? 'air' : 'pluck';
      plan.lead.motif = { ...m, steps, contour: m.contour.slice(0, n), vel, durs: steps.map((s, i) => (i < n - 1 ? Math.min(12, steps[i + 1] - s) : last)) };
    }
  },
  voicing: padVoicing,
  bass: subLine,
  voices(ac, dry, wet, plan, ctx) {
    const o = { registry: ctx.registry, tag: ctx.tag, t0: ctx.t0 };
    return {
      ep: createLiquidPad(ac, dry, wet, plan, { ...o, cap: ctx.cap, r: ctx.r, log: ctx.log }),
      bass: createSub(ac, dry, plan.dnb.bass, o),
      kit: createBreakKit(ac, dry, wet, plan.drums.kit, { ...o, stepDur: ctx.stepDur, lite: ctx.cap <= 10 }),
      lead: plan.lead ? createLiquidLead(ac, dry, wet, plan.lead, plan.dnb.lead, { ...o, stepDur: ctx.stepDur }) : null,
    };
  },
};
